import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AppTable,
  AppButton,
  AppTooltip,
  AppCheckbox,
  AppTableActionButton,
} from '@/components/atoms';
import type { ColumnsType } from 'antd/es/table';
import type { DragEndEvent } from '@dnd-kit/core';
import useAppModal from '@/hooks/useAppModal';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useMainFreeMedicalTreatmentTypes,
  useMainFreeMedicalTreatmentTypeReorder,
} from '@/hooks/main/useMainFreeMedical';
import { useFreeMedicalTreatmentTypeModal } from '@/hooks/main/useFreeMedicalModal';
import {
  FreeMedicalTreatmentType,
  PaginationInfo,
  FreeMedicalInitData,
  FreeMedicalServiceType,
} from '../types';
import { FreeMedicalTreatmentTypeEditModal } from '@/components/organisms';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import { getAntdTableSortOrder } from '@/utils';

const SERVICE_TYPE_LABELS: Record<FreeMedicalServiceType, string> = {
  A2: '鍼灸用',
  R2: 'マッサージ用',
  BOTH: '共有',
};

/** Listeners from useSortable for drag handle (avoids internal @dnd-kit path). */
type DragHandleListeners = Record<string, unknown>;

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: DragHandleListeners;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <AppButton
      type="text"
      size="small"
      icon={<i className="fa fa-bars" aria-hidden="true"></i>}
      className="cursor-move"
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const Row: React.FC<RowProps> = props => {
  const sortableId = String(props['data-row-key']);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'text', // default to text cursor for whole row
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

interface TreatmentTypeTabProps {
  initData: FreeMedicalInitData | null;
}

const TreatmentTypeTab: React.FC<TreatmentTypeTabProps> = ({ initData }) => {
  const queryClient = useQueryClient();
  const [sortField, setSortField] = useState<string>('sort_number');
  const [sortOrder, setSortOrder] = useState<string>('asc');
  const [dataSource, setDataSource] = useState<FreeMedicalTreatmentType[]>([]);

  const {
    treatmentTypeEditModalOpen,
    setTreatmentTypeEditModalOpen,
    selectedTreatmentType,
    isNewTreatmentType,
    handleNewTreatmentType,
    handleEditTreatmentType,
    deleteTreatmentTypeMutation,
    handleSaveTreatmentType,
  } = useFreeMedicalTreatmentTypeModal();

  const { confirm } = useAppModal();

  const handleDeleteTreatmentType = useCallback(
    (treatmentType: FreeMedicalTreatmentType) => {
      confirm({
        title: '自由診療削除確認',
        styles: {
          body: {
            maxWidth: '100%',
            minWidth: 0,
            wordBreak: 'break-all',
            overflowWrap: 'break-word',
          },
        },
        content: (
          <div
            style={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              wordBreak: 'break-all',
              overflowWrap: 'break-word',
              paddingRight: 8,
            }}
          >
            {`自由診療「${treatmentType.name}」を削除しますか？`}
          </div>
        ),
        okText: '削除',
        cancelText: 'キャンセル',
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await deleteTreatmentTypeMutation.mutateAsync(treatmentType.id);
          } catch {
            // error handled by hook
          }
        },
      });
    },
    [confirm, deleteTreatmentTypeMutation]
  );

  const { mutateAsync: reorderTreatmentTypes, isLoading: isReorderingApi } =
    useMainFreeMedicalTreatmentTypeReorder();

  const { data: treatmentTypesResponse, isLoading: loading } = useMainFreeMedicalTreatmentTypes({
    per_page: 100,
    page: 1,
    sort_field: sortField,
    sort_order: sortOrder,
  });

  const treatmentTypes: FreeMedicalTreatmentType[] = treatmentTypesResponse?.data?.data || [];
  const pagination: PaginationInfo = treatmentTypesResponse?.data?.pagination || {
    current_page: 1,
    last_page: 1,
    per_page: 100,
    total: 0,
  };

  useEffect(() => {
    if (!loading && treatmentTypes !== dataSource) {
      setDataSource(treatmentTypes);
    }
  }, [loading, treatmentTypes, dataSource]);

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (active.id === over?.id || !over) {
      return;
    }

    const activeIndex = dataSource.findIndex(record => String(record.id) === String(active.id));
    const overIndex = dataSource.findIndex(record => String(record.id) === String(over.id));

    if (activeIndex === -1 || overIndex === -1) {
      return;
    }

    const newDataSource = arrayMove(dataSource, activeIndex, overIndex);
    setDataSource(newDataSource);

    const sortOrders: Record<number, number> = {};
    newDataSource.forEach((item: FreeMedicalTreatmentType, index: number) => {
      sortOrders[item.id] = index + 1;
    });

    try {
      await reorderTreatmentTypes(sortOrders);
    } catch {
      setDataSource(treatmentTypes);
    }
  };

  const columns: ColumnsType<FreeMedicalTreatmentType> = [
    {
      title: '',
      key: 'sort',
      className: 'w-10',
      align: 'center',
      render: () => <DragHandle />,
    },
    {
      title: '常時',
      dataIndex: 'default_flag',
      key: 'default_flag',
      align: 'center',
      render: (val: boolean | number) => {
        const checked = val === 1 || val === true;
        // Avoid native `disabled` (too faint); use muted accent — slightly brighter than default disabled, not full primary.
        return (
          <AppCheckbox
            checked={checked}
            onChange={() => {}}
            tabIndex={-1}
            aria-disabled={true}
            className="align-middle cursor-default pointer-events-none"
          />
        );
      },
    },
    {
      title: 'カテゴリ名',
      dataIndex: 'free_medical_treatment_category_name',
      key: 'free_medical_treatment_category_name',
      align: 'center',
      width: '30%',
      sorter: true,
      sortOrder: getAntdTableSortOrder(
        sortField,
        'free_medical_treatment_category_name',
        sortOrder
      ),
      render: (_: any, record: FreeMedicalTreatmentType) => {
        const text = record.category?.category_name ?? '-';
        return (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="max-w-full min-w-0 overflow-hidden break-all line-clamp-1">{text}</div>
          </AppTooltip>
        );
      },
    },
    {
      title: 'サービス',
      dataIndex: 'service_type',
      key: 'service_type',
      align: 'center',
      sorter: true,
      sortOrder: getAntdTableSortOrder(sortField, 'service_type', sortOrder),
      render: (val: FreeMedicalServiceType | null) => (val ? SERVICE_TYPE_LABELS[val] : '-'),
    },
    {
      title: '自由診療名',
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: '30%',
      sorter: true,
      sortOrder: getAntdTableSortOrder(sortField, 'name', sortOrder),
      render: (text: string) => (
        <AppTooltip title={text} showOnlyOnOverflow>
          <div className="max-w-full min-w-0 overflow-hidden break-all line-clamp-1">{text}</div>
        </AppTooltip>
      ),
    },
    {
      title: '金額（税込）',
      dataIndex: 'price',
      key: 'price',
      align: 'center',
      render: (val: number) =>
        val === null || val === undefined
          ? ''
          : `${new Intl.NumberFormat('ja-JP').format(Number(val))}円`,
      sorter: true,
      sortOrder: getAntdTableSortOrder(sortField, 'price', sortOrder),
    },
    {
      title: '所要時間',
      dataIndex: 'required_time',
      key: 'required_time',
      align: 'center',
      render: (val: number | null | undefined) =>
        val === null || val === undefined ? '-' : `${val}分`,
      sorter: true,
      sortOrder: getAntdTableSortOrder(sortField, 'required_time', sortOrder),
    },
    {
      title: '操作',
      key: 'actions',
      align: 'center',
      width: 170,
      render: (_: any, record: FreeMedicalTreatmentType) => (
        <div className="flex justify-center gap-1">
          <AppTableActionButton
            action="edit"
            onClick={() => handleEditTreatmentType(record)}
            title="編集"
          >
            編集
          </AppTableActionButton>
          <AppTableActionButton
            action="delete"
            onClick={() => handleDeleteTreatmentType(record)}
            title="削除"
          >
            削除
          </AppTableActionButton>
        </div>
      ),
    },
  ];

  return (
    <CollapsibleCard title="自由診療">
      <div className="flex items-center justify-end mb-4">
        <AppButton
          onClick={handleNewTreatmentType}
          className="flex items-center gap-2"
          type="primary"
        >
          <i className="fa fa-plus" />
          新規登録
        </AppButton>
      </div>
      <div className="overflow-x-auto">
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={dataSource.map(i => String(i.id))}
            strategy={verticalListSortingStrategy}
          >
            <AppTable
              className="w-full text-sm border-collapse"
              scroll={{}}
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              rowKey="id"
              recordCount={pagination.total}
              dragTextName="自由診療一覧"
              loading={loading || isReorderingApi}
              components={{ body: { row: Row } }}
              onChange={(_pagination, _filters, sorter) => {
                if (isReorderingApi) return;
                const sort = Array.isArray(sorter) ? sorter[0] : sorter;

                // If clicking to clear sort (third click), toggle to opposite direction instead
                // to avoid AntD header getting "stuck" with controlled sortOrder.
                if (!sort || !sort.field || !sort.order) {
                  const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                  setSortOrder(newOrder);
                  return;
                }

                const order = sort.order === 'descend' ? 'desc' : 'asc';
                setSortField(sort.field as string);
                setSortOrder(order);
              }}
            />
          </SortableContext>
        </DndContext>
      </div>

      <FreeMedicalTreatmentTypeEditModal
        isOpen={treatmentTypeEditModalOpen}
        treatmentType={selectedTreatmentType}
        isNew={isNewTreatmentType}
        initData={initData}
        onClose={() => {
          setTreatmentTypeEditModalOpen(false);
          queryClient.invalidateQueries({
            predicate: query =>
              query.queryKey[0] === 'free-medical' &&
              query.queryKey[1] === 'treatment-types' &&
              !query.queryKey.includes('detail'),
          });
        }}
        onSave={handleSaveTreatmentType}
      />
    </CollapsibleCard>
  );
};

export default React.memo(TreatmentTypeTab);
